import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type PropsWithChildren,
} from 'react';

export type AnnotationTool = 'freehand' | 'circle' | 'arrow';

export interface AnnotationCanvasContextData {
  isActive: boolean;
  activeTool: AnnotationTool;
  color: string;
  toggleActive: () => void;
  setActive: (active: boolean) => void;
  setActiveTool: (tool: AnnotationTool) => void;
  setColor: (color: string) => void;
  clear: () => void;
  undo: () => void;
  /** Ref to the Fabric.js canvas element for compositing onto screenshots. */
  canvasElRef: React.RefObject<HTMLCanvasElement | null>;
  registerClearHandler: (handler: () => void) => void;
  registerUndoHandler: (handler: () => void) => void;
}

const AnnotationCanvasContext = createContext<
  AnnotationCanvasContextData | undefined
>(undefined);

export function AnnotationCanvasProvider({ children }: PropsWithChildren) {
  const [isActive, setIsActive] = useState(false);
  const [activeTool, setActiveTool] = useState<AnnotationTool>('freehand');
  const [color, setColor] = useState('#ef4444');
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const clearHandlerRef = useRef<(() => void) | null>(null);
  const undoHandlerRef = useRef<(() => void) | null>(null);

  const toggleActive = useCallback(() => {
    setIsActive((prev) => !prev);
  }, []);

  const setActive = useCallback((active: boolean) => {
    setIsActive(active);
  }, []);

  const clear = useCallback(() => {
    clearHandlerRef.current?.();
  }, []);

  const undo = useCallback(() => {
    undoHandlerRef.current?.();
  }, []);

  const registerClearHandler = useCallback((handler: () => void) => {
    clearHandlerRef.current = handler;
  }, []);

  const registerUndoHandler = useCallback((handler: () => void) => {
    undoHandlerRef.current = handler;
  }, []);

  return (
    <AnnotationCanvasContext.Provider
      value={{
        isActive,
        activeTool,
        color,
        toggleActive,
        setActive,
        setActiveTool,
        setColor,
        clear,
        undo,
        canvasElRef,
        registerClearHandler,
        registerUndoHandler,
      }}
    >
      {children}
    </AnnotationCanvasContext.Provider>
  );
}

export function useAnnotationCanvas() {
  const context = useContext(AnnotationCanvasContext);
  if (context === undefined) {
    throw new Error(
      'useAnnotationCanvas must be used within an AnnotationCanvasProvider',
    );
  }
  return context;
}
